'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/providers/AuthProvider';
import { FEEDBACK_CATEGORIES, type FeedbackCategory } from '@/lib/feedback';

const SUBMIT_COOLDOWN_MS = 3000;
const DEFAULT_CATEGORY: FeedbackCategory = 'General Feedback';

const FEEDBACK_TARGETS = [
  'Website',
  'iPhone App',
  'Android App',
  'Billing / Account',
  'Other',
] as const;

type FeedbackTarget = (typeof FEEDBACK_TARGETS)[number];

function defaultFeedbackTarget(urlPlatform: string | undefined): FeedbackTarget {
  const p = urlPlatform?.toLowerCase();
  if (p === 'ios') return 'iPhone App';
  if (p === 'android') return 'Android App';
  return 'Website';
}

function mapTargetToSourcePlatform(
  target: FeedbackTarget,
  urlSource: string
): { source: string; platform: string } {
  switch (target) {
    case 'Website':
      return { source: 'web', platform: 'web' };
    case 'iPhone App':
      return { source: 'app', platform: 'ios' };
    case 'Android App':
      return { source: 'app', platform: 'android' };
    case 'Billing / Account':
      return { source: urlSource, platform: 'account' };
    case 'Other':
      return { source: urlSource, platform: 'other' };
  }
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '16px',
  color: '#333',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '8px',
};

function cardStyle(): React.CSSProperties {
  return {
    maxWidth: '900px',
    margin: '40px auto',
    padding: '30px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  };
}

function FeedbackForm() {
  const searchParams = useSearchParams();
  const { session, loading: authLoading } = useAuth();

  const urlSource = searchParams?.get('source')?.trim() || 'web';
  const urlPlatform = searchParams?.get('platform')?.trim() || undefined;
  const appVersion = searchParams?.get('app_version')?.trim() || undefined;

  const sessionEmail = session?.user?.email?.trim() ?? '';

  const [feedbackTarget, setFeedbackTarget] = useState<FeedbackTarget>(() =>
    defaultFeedbackTarget(urlPlatform)
  );
  const [category, setCategory] = useState<FeedbackCategory>(DEFAULT_CATEGORY);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedCategory, setSubmittedCategory] = useState<FeedbackCategory>(DEFAULT_CATEGORY);
  const [error, setError] = useState<string | null>(null);

  const lastSubmitAtRef = useRef(0);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (sessionEmail) {
      setEmail(sessionEmail);
    }
  }, [sessionEmail]);

  const emailRequired = !sessionEmail;
  const messageTrimmed = message.trim();
  const emailTrimmed = email.trim();

  const canSubmit = useMemo(() => {
    if (isSubmitting || authLoading) return false;
    if (!feedbackTarget || !FEEDBACK_TARGETS.includes(feedbackTarget)) return false;
    if (!category) return false;
    if (!messageTrimmed) return false;
    if (emailRequired && !emailTrimmed) return false;
    return true;
  }, [isSubmitting, authLoading, feedbackTarget, category, messageTrimmed, emailRequired, emailTrimmed]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!feedbackTarget || !FEEDBACK_TARGETS.includes(feedbackTarget)) {
        setError('Please select where this feedback is about.');
        return;
      }
      if (!category) {
        setError('Please select a category.');
        return;
      }
      if (!messageTrimmed) {
        setError('Please enter a message.');
        return;
      }
      if (emailRequired && !emailTrimmed) {
        setError('Please enter your email so we can follow up.');
        return;
      }

      const now = Date.now();
      if (submitInFlightRef.current || now - lastSubmitAtRef.current < SUBMIT_COOLDOWN_MS) {
        setError('Please wait a moment before submitting again.');
        return;
      }

      submitInFlightRef.current = true;
      setIsSubmitting(true);
      lastSubmitAtRef.current = now;

      try {
        const pageUrl = typeof window !== 'undefined' ? window.location.href : undefined;
        const { source, platform } = mapTargetToSourcePlatform(feedbackTarget, urlSource);
        const response = await fetch('/api/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category,
            message: messageTrimmed,
            email: emailTrimmed || undefined,
            source,
            platform,
            app_version: appVersion,
            pageUrl,
          }),
        });

        const data = (await response.json().catch(() => ({}))) as { success?: boolean; error?: string };

        if (response.ok && data.success) {
          setSubmittedCategory(category);
          setIsSubmitted(true);
          setMessage('');
          if (!sessionEmail) {
            setEmail('');
          }
        } else {
          setError(data.error || 'Failed to send feedback. Please try again.');
        }
      } catch (err) {
        console.error('Error sending feedback:', err);
        setError('Error sending feedback. Please try again.');
      } finally {
        setIsSubmitting(false);
        submitInFlightRef.current = false;
      }
    },
    [category, feedbackTarget, messageTrimmed, emailTrimmed, emailRequired, urlSource, appVersion, sessionEmail]
  );

  const resetForm = () => {
    setIsSubmitted(false);
    setError(null);
    setFeedbackTarget(defaultFeedbackTarget(urlPlatform));
    setCategory(DEFAULT_CATEGORY);
    setMessage('');
    if (!sessionEmail) {
      setEmail('');
    }
  };

  if (isSubmitted) {
    return (
      <div style={cardStyle()}>
        <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#059669', marginBottom: '20px', textAlign: 'center' }}>
          Thank you!
        </h2>
        <p style={{ fontSize: '18px', color: '#6b7280', marginBottom: '32px', lineHeight: 1.5, textAlign: 'center' }}>
          Your {submittedCategory.toLowerCase()} has been submitted. We&apos;ll review it and follow up if needed.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            href="/"
            style={{
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            Back to Home
          </Link>
          <button
            type="button"
            onClick={resetForm}
            style={{
              background: '#6b7280',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle()}>
      <div style={{ marginBottom: '32px' }}>
        <Link
          href="/"
          style={{
            display: 'inline-block',
            marginBottom: '20px',
            color: '#3b82f6',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          ← Back to Home
        </Link>
        <h1 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>
          Share your feedback
        </h1>
        <p style={{ fontSize: '18px', color: '#6b7280', lineHeight: 1.5 }}>
          Help us improve Off Axis Deals. Report bugs, request features, or tell us what&apos;s working and what
          isn&apos;t.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="feedbackTarget" style={labelStyle}>
            Where is this feedback about? *
          </label>
          <select
            id="feedbackTarget"
            name="feedbackTarget"
            value={feedbackTarget}
            onChange={(e) => setFeedbackTarget(e.target.value as FeedbackTarget)}
            required
            style={inputStyle}
          >
            {FEEDBACK_TARGETS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="category" style={labelStyle}>
            Category *
          </label>
          <select
            id="category"
            name="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
            required
            style={inputStyle}
          >
            {FEEDBACK_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="message" style={labelStyle}>
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={8}
            required
            placeholder="Describe the issue, idea, or feedback in as much detail as you can."
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="email" style={labelStyle}>
            Email {emailRequired ? '*' : '(optional)'}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={sessionEmail || 'your@email.com'}
            required={emailRequired}
            readOnly={!!sessionEmail && email === sessionEmail}
            style={{
              ...inputStyle,
              ...(sessionEmail && email === sessionEmail
                ? { background: '#f8fafc', color: '#64748b' }
                : {}),
            }}
          />
          {sessionEmail ? (
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>Signed in as {sessionEmail}</p>
          ) : null}
        </div>

        {error ? (
          <div
            role="alert"
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: '100%',
            padding: '15px 20px',
            background: !canSubmit ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 600,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
            opacity: !canSubmit ? 0.7 : 1,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Submit feedback'}
        </button>
      </form>

      <div
        style={{
          marginTop: '32px',
          padding: '20px',
          background: '#f9fafb',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#6b7280',
          lineHeight: 1.6,
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Need immediate help?</p>
        <p style={{ margin: 0 }}>
          Email us at{' '}
          <a href="mailto:support@offaxisdeals.com" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            support@offaxisdeals.com
          </a>
        </p>
      </div>
    </div>
  );
}

export default function FeedbackPage() {
  return (
    <Suspense
      fallback={
        <div style={{ ...cardStyle(), textAlign: 'center', color: '#6b7280' }}>Loading…</div>
      }
    >
      <FeedbackForm />
    </Suspense>
  );
}
